import { produce } from "immer";
import {
  useEffect,
  useState,
  type ErrorInfo,
  type PropsWithChildren,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import {
  defaultErrorBoundaryRetryState,
  ErrorBoundaryRetryStateContext,
} from "./context-error-boundary-retry";
import { ErrorBoundaryFallback } from "./ErrorBoundaryFallback/ErrorBoundaryFallback";

type ErrorBoundaryRetryProps = {
  attemptLimit?: number;
  attemptDelay?: number;
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
};

export function ErrorBoundaryRetry({
  attemptLimit,
  attemptDelay,
  onError,
  onReset,
  children,
}: PropsWithChildren<ErrorBoundaryRetryProps>) {
  const [state, setState] = useState(defaultErrorBoundaryRetryState);

  useEffect(function onComponentMount() {
    setState((prev) =>
      produce(prev, (draft) => {
        draft.onNewAttempt = () => {
          setState((prev) =>
            produce(prev, (draft) => {
              draft.attempts += 1;
            }),
          );
        };
        draft.onResetAttempts = () => {
          setState((prev) =>
            produce(prev, (draft) => {
              draft.attempts = 0;
            }),
          );
        };
      }),
    );
  }, []);

  useEffect(
    function effectOnPropsChanged() {
      setState((prev) =>
        produce(prev, (draft) => {
          draft.attemptLimit = attemptLimit ?? prev.attemptLimit;
          draft.attemptDelay = attemptDelay ?? prev.attemptDelay;
          draft.onError = onError ?? prev.onError;
          draft.onReset = onReset ?? prev.onReset;
        }),
      );
    },
    [attemptLimit, attemptDelay, onError, onReset],
  );

  return (
    <ErrorBoundaryRetryStateContext.Provider value={state}>
      <ErrorBoundary
        FallbackComponent={ErrorBoundaryFallback}
        onError={state.onError}
        onReset={state.onReset}
      >
        {children}
      </ErrorBoundary>
    </ErrorBoundaryRetryStateContext.Provider>
  );
}
